'use client';

import { Plus } from 'assets/icons';
import React, { createContext, useContext, useCallback } from 'react';
import styles from './Accordion.module.css';

type AccordionContextValue = {
  value: string | undefined;
  onValueChange: (value: string) => void;
  type: 'single' | 'multiple';
  collapsible?: boolean;
};

const AccordionContext = createContext<AccordionContextValue | undefined>(
  undefined,
);

const AccordionItemContext = createContext<{ value: string } | undefined>(
  undefined,
);

interface AccordionProps {
  type: 'single' | 'multiple';
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  collapsible?: boolean;
  children: React.ReactNode;
}

export function Accordion({
  type = 'single',
  value,
  defaultValue,
  onValueChange,
  collapsible = false,
  children,
  ...props
}: AccordionProps) {
  const [internalValue, setInternalValue] = React.useState<string | undefined>(
    value ?? defaultValue,
  );

  const handleValueChange = useCallback(
    (newValue: string) => {
      if (onValueChange) {
        onValueChange(newValue);
      } else {
        setInternalValue(newValue);
      }
    },
    [onValueChange],
  );

  const currentValue = value ?? internalValue;

  return (
    <AccordionContext.Provider
      value={{
        value: currentValue,
        onValueChange: handleValueChange,
        type,
        collapsible,
      }}
    >
      <div {...props} className={styles.root}>
        {children}
      </div>
    </AccordionContext.Provider>
  );
}

export const AccordionConsumer = AccordionContext.Consumer;

interface AccordionItemProps {
  value: string;
  children: React.ReactNode;
}

export const AccordionItem = React.forwardRef<
  HTMLDivElement,
  AccordionItemProps
>(({ value, children, ...props }, ref) => {
  return (
    <AccordionItemContext.Provider value={{ value }}>
      <div ref={ref} {...props} className={styles.item}>
        {children}
      </div>
    </AccordionItemContext.Provider>
  );
});

AccordionItem.displayName = 'AccordionItem';

interface AccordionTriggerProps {
  children: React.ReactNode;
}

export function AccordionTrigger({
  children,
  ...props
}: AccordionTriggerProps) {
  const accordion = useContext(AccordionContext);
  const item = useContext(AccordionItemContext);

  if (!accordion || !item) {
    throw new Error('AccordionTrigger must be used within an Accordion');
  }

  const isOpen = accordion.value === item.value;

  const handleClick = () => {
    if (accordion.type === 'single') {
      if (accordion.collapsible && isOpen) {
        accordion.onValueChange('');
      } else {
        accordion.onValueChange(item.value);
      }
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      data-state={isOpen ? 'open' : 'closed'}
      {...props}
      className={styles.trigger}
    >
      {children}
      <Plus fill="#A1A1A1" style={{ width: '16px', height: '16px' }} />
    </button>
  );
}

interface AccordionContentProps {
  children: React.ReactNode;
}

export function AccordionContent({
  children,
  ...props
}: AccordionContentProps) {
  const accordion = useContext(AccordionContext);
  const item = useContext(AccordionItemContext);

  if (!accordion || !item) {
    throw new Error('AccordionContent must be used within an Accordion');
  }

  const isOpen = accordion.value === item.value;

  return (
    <div data-state={isOpen ? 'open' : 'closed'} {...props} className={styles.content}>
      {children}
    </div>
  );
}
